import React from "react";
import { useTranslation } from "@i18n";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { convertDate } from "@Services/luxonHelper";

/**
 * One entry of `checklistsInReviewsResponse.history`, which the server declares as
 * `z.array(z.unknown())` (and `checklistsInReviews` itself is `}: any)` server-side), so there is no
 * server type to flow here. Shape taken from the `History` writes in reviews.service.ts.
 */
export interface ChecklistHistoryEntry {
  id?: string;
  userId?: string;
  createdDate?: number;
  actionType?: string;
  displayOldLabels?: string;
  displayNewLabels?: string;
  oldSelection?: string[];
  newSelection?: string[];
  commentText?: string;
  oldCommentText?: string;
  commentOwnerId?: string;
}

/**
 * One checklist answer choice. `manageChecklistRequest.choices` is
 * `z.array(z.record(z.string(), z.unknown()))`, so the label/id pair is not typed server-side either.
 */
export interface ChecklistChoice {
  _id?: string;
  value?: string;
}

interface HistoryTabProps {
  history: ChecklistHistoryEntry[];
  choices?: ChecklistChoice[];
}

const HistoryTab: React.FC<HistoryTabProps> = ({ history, choices = [] }) => {
  const { t } = useTranslation();

  if (history.length === 0) {
    return <div className="text-center text-gray-400 text-sm py-10 px-6">{t("No history available.")}</div>;
  }

  const choiceMap = new Map(choices.map((c) => [c._id?.toString(), c]));

  const getChoiceLabel = (choiceId: string): string => {
    const choice = choiceMap.get(choiceId);
    // KNOWN BUG, preserved verbatim: a selection id that is not in `choices` (e.g. a choice deleted
    // from the checklist template after the answer was recorded) makes `.get` return undefined and
    // this throws, blanking the whole History tab. Does the server read it? No — render-only. Fix:
    // fall back to the raw id, which would add a `?.`/`??` to the emit, so it is left alone.
    return choice!.value!;
  };

  const getSelectionLabels = (selection: string[] = []): string => {
    if (selection.length === 0) return "None";
    return selection.map(getChoiceLabel).join(", ");
  };

  const renderAction = (entry: ChecklistHistoryEntry) => {
    if (entry.actionType === "selection_changed") {
      const oldLabels = entry.displayOldLabels || getSelectionLabels(entry.oldSelection);
      const newLabels = entry.displayNewLabels || getSelectionLabels(entry.newSelection);
      const oldSelectionCount = entry.oldSelection?.length ?? 0;

      if (oldSelectionCount === 0) {
        return (
          <span>
            {t("marked the response as")} <b>{newLabels}</b>.
          </span>
        );
      } else {
        return (
          <span>
            {t("changed the response from")} <b>{oldLabels}</b> {t("to")} <b>{newLabels}</b>.
          </span>
        );
      }
    }

    if (entry.actionType === "comment_added") {
      return (
        <span>
          {t("added a comment:")} &quot;<b>{entry.commentText}</b>&quot;{"."}
        </span>
      );
    }

    if (entry.actionType === "comment_edited") {
      return (
        <span>
          {t("edited a comment from")} &quot;<b>{entry.oldCommentText}</b>&quot; {t("to")} &quot;
          <b>{entry.commentText}</b>&quot;.
        </span>
      );
    }

    if (entry.actionType === "comment_deleted") {
      return (
        <span>
          {t("deleted a comment:")} &quot;<b>{entry.commentText}</b>&quot;{"."}
        </span>
      );
    }

    if (entry.actionType === "comment_marked_official") {
      if (entry.commentOwnerId) {
        return (
          <span>
            {t("marked the comment:")} &quot;<b>{entry.commentText}</b>&quot; {t("made by")}{" "}
            <UserNameTagComponent id={entry.commentOwnerId} /> {t("as official response")}
            {"."}
          </span>
        );
      }
      return (
        <span>
          {t("marked the comment:")} &quot;<b>{entry.commentText}</b>&quot; {t("as official response")}
          {"."}
        </span>
      );
    }

    if (entry.actionType === "comment_unmarked_official") {
      if (entry.commentOwnerId) {
        return (
          <span>
            {t("unmarked the comment:")} &quot;<b>{entry.commentText}</b>&quot; {t("made by")}{" "}
            <UserNameTagComponent id={entry.commentOwnerId} /> {t("from official response")}
            {"."}
          </span>
        );
      }
    }
  };

  return (
    <div className="p-2 px-2 max-h-[37.5rem] overflow-y-auto">
      {history.map((entry: ChecklistHistoryEntry, index: number) => (
        <div key={entry.id ?? index} className="flex gap-3">
          <div className="text-xs text-gray-500 whitespace-nowrap pt-3">
            {convertDate(Number(entry.createdDate), "dateForText")}
          </div>
          <div className="flex flex-col items-center">
            {index !== 0 ? <div className="w-0.5 bg-gray-300 h-3" /> : <div className="h-3" />}
            <div className="w-3 h-3 rounded-full bg-gray-400 flex-shrink-0" />
            {index !== history.length - 1 && <div className="w-0.5 bg-gray-300 flex-1 min-h-1" />}
          </div>
          <div className="flex items-center gap-2 pb-3 flex-1 pt-1">
            <UserNameTagComponent id={entry.userId} />
            <div className="text-sm text-gray-700">{renderAction(entry)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HistoryTab;
